import { Check, Loader2, Pencil, X } from 'lucide-react'
import type { ComponentType } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'

export interface EditableFieldProps {
  label: string
  icon: ComponentType<{ className?: string }>
  value: string | undefined
  onSave: (next: string) => Promise<void>
  monospace?: boolean
  fullWidth?: boolean
}

export function EditableField({
  label,
  icon: Icon,
  value,
  onSave,
  monospace,
  fullWidth,
}: EditableFieldProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = () => {
    setDraft(value ?? '')
    setError(null)
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setError(null)
  }

  const commit = async () => {
    const next = draft.trim()
    if (next === (value ?? '').trim()) {
      cancel()
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('identity.detail.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  const valueClasses = monospace
    ? 'font-mono font-medium text-foreground'
    : 'font-medium text-foreground'

  if (editing) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg bg-muted border border-primary/40 ring-1 ring-primary/20',
          fullWidth && 'md:col-span-2'
        )}
      >
        <Icon className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <div className="flex items-center gap-1 mt-1">
            <Input
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') void commit()
                if (e.key === 'Escape') cancel()
              }}
              disabled={saving}
              className="h-9 md:h-7"
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9 md:h-7 md:w-7"
              onClick={() => void commit()}
              disabled={saving}
              aria-label={t('identity.detail.save')}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5 text-success" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9 md:h-7 md:w-7"
              onClick={cancel}
              disabled={saving}
              aria-label={t('identity.detail.cancel')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      aria-label={t('identity.detail.edit', { label })}
      className={cn(
        'group flex items-center gap-3 p-3 rounded-lg bg-muted text-left w-full',
        'border border-transparent hover:border-primary/40 hover:bg-muted/80 transition-colors cursor-pointer',
        fullWidth && 'md:col-span-2'
      )}
    >
      <Icon className="h-5 w-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p
          className={cn(
            'truncate',
            valueClasses,
            !value && 'text-muted-foreground italic font-normal'
          )}
        >
          {value || t('identity.detail.clickToAdd')}
        </p>
      </div>
      <Pencil className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-primary transition-colors shrink-0" />
    </button>
  )
}
